import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import {
  Backpack,
  BookOpen,
  Check,
  ChevronDown,
  FlaskConical,
  GraduationCap,
  Info,
  Languages,
  Package,
  RotateCcw
} from 'lucide-react';
import { db } from '../../core/firebase/firebase';
import {
  SCHOOL_SUPPLY_NOTICES,
  SUPPLY_SECTIONS,
  levelKeyFromStudent,
  levelLabel,
  normalizeSchoolYear,
  schoolYearLabel,
  suppliesListId
} from './schoolSuppliesUtils';

function studentName(student) {
  return student?.fullName || student?.fullname || student?.studentFullName || student?.name || 'Votre enfant';
}

function normalizeItem(item) {
  return {
    ...item,
    section: item.section || item.category || SUPPLY_SECTIONS[0],
    subject: item.subject || '',
    cover: item.cover || '',
    note: item.note || ''
  };
}

function sectionVisual(section) {
  const value = String(section || '').toLowerCase();
  if (value.includes('العربية') || value.includes('arabe')) return { icon: BookOpen, tone: 'arabic' };
  if (value.includes('علوم') || value.includes('science')) return { icon: FlaskConical, tone: 'science' };
  if (value.includes('لغات') || value.includes('langue')) return { icon: Languages, tone: 'languages' };
  if (value.includes('تربية') || value.includes('éducation')) return { icon: GraduationCap, tone: 'education' };
  return { icon: Package, tone: 'common' };
}

function hasArabic(value) {
  return /[\u0600-\u06FF]/.test(String(value || ''));
}

function coverTone(value) {
  const text = String(value || '').toLowerCase();
  if (text.includes('أحمر') || text.includes('احمر') || text.includes('rouge')) return 'red';
  if (text.includes('أصفر') || text.includes('اصفر') || text.includes('jaune')) return 'yellow';
  if (text.includes('برتقالي') || text.includes('orange')) return 'orange';
  if (text.includes('أخضر') || text.includes('اخضر') || text.includes('vert')) return 'green';
  if (text.includes('وردي') || text.includes('rose')) return 'pink';
  if (text.includes('أزرق') || text.includes('ازرق') || text.includes('bleu')) return 'blue';
  if (text.includes('بنفسجي') || text.includes('violet')) return 'purple';
  if (text.includes('شفاف') || text.includes('transparent')) return 'clear';
  return 'neutral';
}

export default function ParentSchoolSupplies({ selectedChild, user, parentId, defaultSchoolYear }) {
  const levelKey = levelKeyFromStudent(selectedChild);
  const schoolYear = normalizeSchoolYear(selectedChild?.schoolYear || defaultSchoolYear);
  const listId = levelKey ? suppliesListId(schoolYear, levelKey) : '';
  const [list, setList] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [openSections, setOpenSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setList(null);
    setCheckedItems({});
    setError('');
    if (!listId) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const listRef = doc(db, 'schoolSupplies', listId);
    const unsubscribeList = onSnapshot(listRef, (snapshot) => {
      if (snapshot.exists() && snapshot.data().published === true) {
        const data = snapshot.data();
        const items = Array.isArray(data.items)
          ? [...data.items]
              .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
              .map(normalizeItem)
          : [];
        setList({ id: snapshot.id, ...data, items });
        const defaults = {};
        items.forEach((item) => { defaults[item.section] = true; });
        setOpenSections((current) => ({ ...defaults, ...current }));
      } else {
        setList(null);
      }
      setLoading(false);
    }, (loadError) => {
      console.warn('ParentSchoolSupplies — list:', loadError);
      setList(null);
      setLoading(false);
    });

    const progressId = `${user.uid}_${selectedChild.id}_${listId}`;
    const progressRef = doc(db, 'users', user.uid, 'schoolSuppliesProgress', progressId);
    const unsubscribeProgress = onSnapshot(progressRef, (snapshot) => {
      setCheckedItems(snapshot.exists() && snapshot.data().checkedItems ? snapshot.data().checkedItems : {});
    }, (progressError) => {
      console.warn('ParentSchoolSupplies — progress:', progressError);
    });

    return () => {
      unsubscribeList();
      unsubscribeProgress();
    };
  }, [listId, selectedChild?.id, user?.uid]);

  const grouped = useMemo(() => {
    if (!list?.items?.length) return [];
    const order = [...SUPPLY_SECTIONS];
    list.items.forEach((item) => {
      if (!order.includes(item.section)) order.push(item.section);
    });

    return order.map((section) => {
      const rows = list.items.filter((item) => item.section === section);
      if (!rows.length) return null;
      const subjectOrder = [];
      rows.forEach((item) => {
        const subject = item.subject || '';
        if (!subjectOrder.includes(subject)) subjectOrder.push(subject);
      });
      return {
        section,
        rows,
        subjects: subjectOrder.map((subject) => ({
          subject,
          rows: rows.filter((item) => (item.subject || '') === subject)
        }))
      };
    }).filter(Boolean);
  }, [list]);

  const total = list?.items?.length || 0;
  const checkedCount = list?.items?.filter((item) => checkedItems[item.id] === true).length || 0;
  const percent = total ? Math.round((checkedCount / total) * 100) : 0;

  async function persist(nextChecked) {
    if (!listId || !selectedChild?.id || !user?.uid) return;
    setSaving(true);
    setError('');
    const progressId = `${user.uid}_${selectedChild.id}_${listId}`;
    try {
      await setDoc(doc(db, 'users', user.uid, 'schoolSuppliesProgress', progressId), {
        ownerUid: user.uid,
        parentId: parentId || null,
        studentId: selectedChild.id,
        listId,
        levelKey,
        schoolYear,
        checkedItems: nextChecked,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (saveError) {
      console.error(saveError);
      setError('La case n’a pas pu être enregistrée. Réessayez.');
    } finally {
      setSaving(false);
    }
  }

  function toggleItem(itemId) {
    const next = { ...checkedItems, [itemId]: checkedItems[itemId] !== true };
    setCheckedItems(next);
    persist(next);
  }

  function resetAll() {
    const next = { ...checkedItems };
    (list?.items || []).forEach((item) => { next[item.id] = false; });
    setCheckedItems(next);
    persist(next);
  }

  if (!levelKey) {
    return <section>
      <div className="parent-section-title"><p className="eyebrow">PRÉPARATION DE LA RENTRÉE</p><h2>Fournitures scolaires</h2><p>La classe de {studentName(selectedChild)} doit être confirmée avant d’afficher la liste.</p></div>
      <div className="parent-empty large">Aucune classe reconnue pour cet enfant. Contactez l’administration si nécessaire.</div>
    </section>;
  }

  if (loading) {
    return <section><div className="parent-section-title"><p className="eyebrow">PRÉPARATION DE LA RENTRÉE</p><h2>Fournitures scolaires</h2></div><div className="parent-empty large">Chargement de la liste…</div></section>;
  }

  if (!list) {
    return <section>
      <div className="parent-section-title"><p className="eyebrow">PRÉPARATION DE LA RENTRÉE</p><h2>Fournitures scolaires</h2><p>{studentName(selectedChild)} · {levelLabel(levelKey)} · {schoolYearLabel(schoolYear)}</p></div>
      <div className="parent-empty large"><Backpack size={28}/><strong>La liste n’est pas encore disponible.</strong><p>L’école la publiera dès qu’elle sera prête.</p></div>
    </section>;
  }

  const notices = Array.isArray(list.notices) && list.notices.length ? list.notices : SCHOOL_SUPPLY_NOTICES;

  return <section className="parent-supplies-page parent-supplies-v3">
    <div className="parent-section-title parent-supplies-heading">
      <div><p className="eyebrow">PRÉPARATION DE LA RENTRÉE</p><h2>Fournitures scolaires</h2><p>{studentName(selectedChild)} · {levelLabel(levelKey)} · {schoolYearLabel(schoolYear)}</p></div>
      {checkedCount > 0 && <button type="button" className="button button-secondary" onClick={resetAll} disabled={saving}><RotateCcw size={16}/> Tout décocher</button>}
    </div>

    {error && <div className="alert alert-error">{error}</div>}

    <article className={`parent-card-v0 parent-supplies-progress-card ${percent === 100 ? 'complete' : ''}`}>
      <div className="parent-supplies-progress-copy">
        <span className="parent-supplies-icon"><Backpack size={22}/></span>
        <div><strong>{percent === 100 ? 'Liste complète 🎉' : `${checkedCount} / ${total} fournitures achetées`}</strong><span>{percent === 100 ? 'Tout est prêt pour la rentrée.' : 'Cochez chaque article au fur et à mesure de vos achats.'}</span></div>
        <b>{percent}%</b>
      </div>
      <div className="parent-supplies-progress"><div style={{ width: `${percent}%` }}/></div>
    </article>

    <article className="parent-card-v0 parent-supplies-notices" dir="rtl">
      <Info size={19}/>
      <div>{notices.map((notice) => <p key={notice}>{notice}</p>)}</div>
    </article>

    <div className="parent-supplies-groups">
      {grouped.map(({ section, rows, subjects }) => {
        const sectionChecked = rows.filter((item) => checkedItems[item.id] === true).length;
        const sectionPercent = rows.length ? Math.round((sectionChecked / rows.length) * 100) : 0;
        const isOpen = openSections[section] !== false;
        const { icon: SectionIcon, tone } = sectionVisual(section);

        return <article className={`parent-card-v0 parent-supplies-category supply-tone-${tone}`} key={section}>
          <button type="button" className="parent-supplies-category-head" onClick={() => setOpenSections((current) => ({ ...current, [section]: !isOpen }))}>
            <span className="parent-supplies-section-icon"><SectionIcon size={19}/></span>
            <div className="parent-supplies-category-copy" dir="auto">
              <strong>{section}</strong>
              <span>{sectionChecked} / {rows.length} acheté{rows.length > 1 ? 's' : ''}</span>
              <span className="parent-supplies-mini-progress" aria-hidden="true"><i style={{ width: `${sectionPercent}%` }}/></span>
            </div>
            <span className="parent-supplies-section-percent">{sectionPercent}%</span>
            <ChevronDown className={isOpen ? 'open' : ''} size={19}/>
          </button>

          {isOpen && <div className="parent-supplies-items">
            {subjects.map(({ subject, rows: subjectRows }) => {
              const subjectChecked = subjectRows.filter((item) => checkedItems[item.id] === true).length;
              return <div className="parent-supply-subject-block" key={`${section}-${subject || 'common'}`}>
                {subject && <div className="parent-supply-subject-title" dir="auto">
                  <span>{subject}</span>
                  <small>{subjectChecked}/{subjectRows.length}</small>
                </div>}

                <div className="parent-supply-subject-items">
                  {subjectRows.map((item) => {
                    const checked = checkedItems[item.id] === true;
                    const arabic = hasArabic(item.name) || hasArabic(item.cover) || hasArabic(item.note);
                    return <label className={`parent-supply-item ${checked ? 'checked' : ''} ${arabic ? 'is-arabic' : ''}`} key={item.id}>
                      <input type="checkbox" checked={checked} onChange={() => toggleItem(item.id)} disabled={saving}/>
                      <span className="parent-supply-checkbox">{checked && <Check size={16}/>}</span>
                      <span className="parent-supply-text" dir="auto">
                        <span className="parent-supply-main-line">
                          {Number(item.quantity || 1) > 1 && <b className="parent-supply-qty">{item.quantity}×</b>}
                          <strong>{item.name}</strong>
                        </span>
                        {(item.cover || item.note) && <span className="parent-supply-meta">
                          {item.cover && <em dir="auto" className={`cover cover-${coverTone(item.cover)}`}>{item.cover}</em>}
                          {item.note && <em dir="auto" className="note">{item.note}</em>}
                        </span>}
                      </span>
                    </label>;
                  })}
                </div>
              </div>;
            })}
          </div>}
        </article>;
      })}
    </div>
  </section>;
}
